import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Printer, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const TIME_SLOTS = ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'];
const SATURDAY_SLOTS = ['09:00', '10:30', '12:00']; // Samedi: matin seulement
const SUNDAY_SLOTS = ['09:00', '10:30', '12:00']; // Dimanche: matin seulement
const CLOSED_SLOTS = {
  'Samedi': ['16:30', '17:30', '18:30', '19:30'],
  'Dimanche': ['16:30', '17:30', '18:30', '19:30']
};

export default function FinalSchedule({ token }) {
  const [currentWeek, setCurrentWeek] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentWeek(getNextMonday());
  }, []);

  useEffect(() => {
    if (currentWeek) {
      fetchBookings();
    }
  }, [currentWeek]);

  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    const hour = today.getHours();
    
    let daysUntilMonday;
    
    // PENDANT LA FENÊTRE DE RÉSERVATION (Samedi 11h → Dimanche 20h)
    // On réserve pour LA SEMAINE PROCHAINE
    if ((dayOfWeek === 6 && hour >= 11) || dayOfWeek === 0) {
      // Samedi après 11h OU Dimanche (peu importe l'heure jusqu'à 20h)
      // → Aller au PROCHAIN lundi
      if (dayOfWeek === 6) {
        // Samedi → Lundi suivant = +2 jours
        daysUntilMonday = 2;
      } else {
        // Dimanche → Lundi suivant = +1 jour
        daysUntilMonday = 1;
      }
    } 
    // HORS FENÊTRE DE RÉSERVATION
    // On affiche la semaine actuelle
    else if (dayOfWeek === 1) {
      // Lundi → ce lundi
      daysUntilMonday = 0;
    } else if (dayOfWeek === 0) {
      // Dimanche avant réouverture → semaine actuelle
      daysUntilMonday = 1;
    } else {
      // Mardi-Vendredi ou Samedi avant 11h → Lundi de cette semaine
      daysUntilMonday = -(dayOfWeek - 1);
    }
    
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysUntilMonday);
    return monday.toISOString().split('T')[0];
  };

  const formatWeekRange = (mondayDate) => {
    // Parse date in UTC to avoid timezone issues
    const [year, month, day] = mondayDate.split('-').map(Number);
    const monday = new Date(year, month - 1, day);
    const sunday = new Date(year, month - 1, day + 6);
    
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return `Semaine du ${monday.toLocaleDateString('fr-FR', options)} au ${sunday.toLocaleDateString('fr-FR', options)}`;
  };

  const changeWeek = (direction) => {
    const [year, month, day] = currentWeek.split('-').map(Number);
    const current = new Date(year, month - 1, day);
    current.setDate(current.getDate() + (direction * 7));
    
    // Format back to YYYY-MM-DD
    const newYear = current.getFullYear();
    const newMonth = String(current.getMonth() + 1).padStart(2, '0');
    const newDay = String(current.getDate()).padStart(2, '0');
    setCurrentWeek(`${newYear}-${newMonth}-${newDay}`);
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${currentWeek}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const getClientsForSlot = (day, time) => {
    return bookings.filter(b => b.day === day && b.time_slot === time);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    toast.success('Export PDF - Utilisez Imprimer > Enregistrer au format PDF');
    window.print();
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-12 text-center text-zinc-400">
          Chargement du planning...
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Actions Bar - Hidden when printing */}
      <Card className="bg-zinc-900 border-zinc-800 mb-6 print:hidden">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeWeek(-1)}
                className="bg-zinc-800 border-zinc-700"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentWeek(getNextMonday())}
                className="bg-zinc-800 border-zinc-700 text-white"
              >
                Cette semaine
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeWeek(1)}
                className="bg-zinc-800 border-zinc-700"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handlePrint}
                className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
              >
                <Printer className="mr-2 h-4 w-4" />
                Imprimer
              </Button>
              <Button
                onClick={handleExportPDF}
                className="bg-red-600 hover:bg-red-700"
              >
                <Download className="mr-2 h-4 w-4" />
                Exporter PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Table - Optimized for printing */}
      <div className="bg-white text-black rounded-lg print:shadow-none print:m-0" style={{ margin: '0', padding: '2rem' }}>
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-2">Horaire de la Semaine</h1>
          <p className="text-gray-600">{formatWeekRange(currentWeek)}</p>
          <p className="text-sm text-gray-500 mt-1">EM Coaching - Berchem-Sainte-Agathe</p>
        </div>

        {/* Main Schedule Table */}
        <table className="w-full border-collapse mb-6">
          <thead>
            <tr className="bg-amber-100">
              <th className="border-2 border-gray-400 p-3 w-24 font-bold">H / J</th>
              {DAYS.map(day => (
                <th key={day} className="border-2 border-gray-400 p-3 font-bold">
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIME_SLOTS.map((time, idx) => {
              // Add pause row after 12:00
              const rows = [];
              
              rows.push(
                <tr key={time} className={idx % 2 === 0 ? 'bg-amber-50' : 'bg-white'}>
                  <td className="border-2 border-gray-400 p-3 font-bold text-center bg-amber-100">
                    {time}
                  </td>
                  {DAYS.map(day => {
                    // Check if slot is closed for this day
                    const isClosed = CLOSED_SLOTS[day]?.includes(time);
                    
                    if (isClosed) {
                      return (
                        <td key={day} className="border-2 border-gray-400 p-3 align-top bg-gray-300">
                          <div className="text-gray-500 text-center text-sm">Fermé</div>
                        </td>
                      );
                    }
                    
                    const clients = getClientsForSlot(day, time);
                    // Dédupliquer les clients par email
                    const uniqueClients = Array.from(
                      new Map(clients.map(b => [b.client.email, b])).values()
                    );
                    
                    return (
                      <td key={day} className="border-2 border-gray-400 p-3 align-top">
                        {uniqueClients.length > 0 ? (
                          <div className="font-medium">
                            {uniqueClients.map((booking, i) => (
                              <span key={booking.client.email}>
                                {booking.client.first_name}
                                {i < uniqueClients.length - 1 && <span className="text-gray-500"> / </span>}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-gray-300 text-center">-</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );

              // Add pause row after 12:00
              if (time === '12:00') {
                rows.push(
                  <tr key="pause" className="bg-gray-200">
                    <td colSpan={7} className="border-2 border-gray-400 p-2 text-center font-bold">
                      Pause
                    </td>
                  </tr>
                );
              }

              return rows;
            })}
          </tbody>
        </table>

        {/* Weekend Note */}
        <div className="mt-6 p-4 bg-gray-100 rounded border-2 border-gray-300">
          <h3 className="font-bold mb-2">Note</h3>
          <div className="text-sm space-y-1">
            <p><strong>Samedi :</strong> Matin uniquement (09:00-12:00) - Après-midi fermé</p>
            <p><strong>Dimanche :</strong> Fermé - Période de réservation (11h00-20h00)</p>
          </div>
        </div>

        {/* Footer Notes */}
        <div className="mt-6 text-xs text-gray-500 text-center print:block">
          <p>Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
          <p className="mt-1">EM Coaching - Contact: 0489 82 27 09</p>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          * {
            margin: 0 !important;
            padding: 0 !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            size: A4 landscape;
            margin: 0.5cm;
          }
        }
        
        /* Mobile optimization - remove white borders */
        @media (max-width: 768px) {
          body {
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .bg-black {
            background: white !important;
          }
        }
      `}</style>
    </>
  );
}
